import React, { ReactNode, useEffect, useState } from "react";
import { Spinner } from "components/Spinner/Spinner";
import { TableInfo } from "api";
import { CardPrototype } from "components/CardPage/CardPrototype";
import { CardActions } from "components/Category/ParentCrudMenu/ParentCrudMenu";
import { NestedListItemActionTypes } from "components/Category/Category";
import { Transition } from "react-transition-group";
import { customUseOnClickOutside } from "components/helpers/CustomFieldRenderers/SearchListFieldRenderer/SearchListRenderer";

import closeIcon from "assets/Icon_Btn_06.png";
import leftArrowIcon from "assets/Icon_Btn_Previous.png";
import rightArrowIcon from "assets/Icon_Btn_Next.png";

export type MenuOnClicks = {
  view: (id: number, allData: any) => void;
  update: (id: number, allData: any) => void;
  delete: (id: number, allData: any) => void;
  projectmanagement?: (id: number, allData: any) => void;
  parent?: CardActions;
};

export type CardDataInformation = {
  mainInfoColumns: string[];
  listInfo: { title: string; column: string }[];
};

export type CardLayout3DataInformation = {
  text1: string | null;
  text2: string | null;
};

export type CardLayout4DataInformation = {
  description: string | null;
};

export type CardLayoutCustomFieldsDataInformation = {
  mainInfoColumns: string[];
  listInfo: { title: string; column: string }[];
};

export type CardLayoutGatewayDataInformation = {
  text1: string;
  text2: string;
  listInfo: string[];
};

export type CardPageMenuData = {
  text: string;
  onClick: (id: number, allData: any) => void;
  permissionsKey: string;
}[];

export type CardTypeOptions =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | "Gateway"
  | "CustomFields"
  | "ProjectManagement";

type CardPageProps<Data> = {
  idColumn: string;
  table?: TableInfo;

  titleColumn?: string | string[];
  typeColumn?: string;
  includeTypeColumn?: boolean;
  type2Column?: string;
  includeType2Column?: boolean;

  layout?: CardTypeOptions;

  customIcon?: (props?: any) => ReactNode;
  customContent?: (props?: any) => ReactNode;
  contentClassname?: string;
  actionType?: Partial<NestedListItemActionTypes>;
  footerButtonText?: string;
  footerButtonDisabled?: boolean;
  filterCardData?: { [columnName: string]: any }; // filter card data by the given column names and corresponding values. Also, in the case of multiple columns, can specify whether all need to be valid to filter, or whether any/some is enough to filter.
  projectID?: boolean;
  clientID?: boolean;
  maxTitleLength?: number;
  centerCardPageTitle?: boolean;
  hideIcon?: boolean;
  crudItems: any;
  permissions: any;
  disableMenuItemData: any;
  rolePermissions?: any;
  oppositePermissions?: any;
  LargeRightPanel?: boolean | ((action?: string | null) => boolean);
  combineTitleAndType?: boolean;
  hideMenuOptions?: boolean;
  imageCardColumn?: string;
  overrideFooterButtonText?: string;
};

type ParsedCardDataInformationListInfo = { title: string; text: string }[];

type ParsedGatewayCardDataInformationListInfo = string[];

export type ParsedCardDataInformation = {
  mainText: string;
  listInfo: ParsedCardDataInformationListInfo;
};

export type ParsedCardLayout3DataInformation = {
  text1: string | null;
  text2: string | null;
};

export type ParsedCardLayout4DataInformation = {
  description: string | null;
};

export type ParsedCardLayoutCustomFieldsDataInformation = {
  mainText: string;
  listInfo: ParsedCardDataInformationListInfo;
};

export type ParsedCardLayoutGatewayDataInformation = {
  text1: string;
  text2: string;
  listInfo: ParsedGatewayCardDataInformationListInfo;
};

type CardData = {
  id: number | null;
  title: string;
  type: string;
  type2: string;
  allData: any;
};

type SortData = {
  [column: string]: string;
};

export type FullscreenImage = {
  index: number;
  filePath: string | null;
};

function CardPage<Data>({
  idColumn,
  table,

  titleColumn,
  typeColumn,
  includeTypeColumn,
  type2Column,
  includeType2Column,

  layout = 1,

  customContent,
  customIcon,
  contentClassname,
  actionType = "edit",
  footerButtonText,
  footerButtonDisabled,
  filterCardData,
  projectID,
  clientID,
  maxTitleLength,
  centerCardPageTitle,
  hideIcon,
  crudItems,
  permissions,
  disableMenuItemData,
  rolePermissions,
  oppositePermissions,
  LargeRightPanel,
  combineTitleAndType,
  hideMenuOptions,
  imageCardColumn,
  overrideFooterButtonText,
}: CardPageProps<Data>) {
  const [id, setId] = useState<number | null>(null);
  const [cardData, setCardData] = useState<CardData[] | null>(null);
  const [sortData, setSortData] = useState<SortData | null>(null);
  const [
    fullScreenImage,
    setFullscreenImage,
  ] = useState<FullscreenImage | null>(null);

  const [delayedOpen, setDelayedOpen] = React.useState(false);
  React.useEffect(() => {
    if (fullScreenImage && !delayedOpen) {
      setTimeout(() => {
        setDelayedOpen(true);
      }, 1);
    }
  }, [fullScreenImage]);

  const bind = customUseOnClickOutside(() => setFullscreenImage(null), true);

  const previousImage = (e: React.MouseEvent) => {
    if (
      fullScreenImage &&
      typeof fullScreenImage.index === "number" &&
      fullScreenImage.index > 0
    ) {
      setFullscreenImage({
        index: fullScreenImage.index - 1,
        filePath: fullScreenImage?.filePath,
      });
    }
  };

  const nextImage = (e: React.MouseEvent) => {
    if (fullScreenImage && typeof fullScreenImage.index === "number") {
      if (cardData && fullScreenImage.index + 1 <= cardData?.length) {
        setFullscreenImage({
          index: fullScreenImage.index + 1,
          filePath: fullScreenImage?.filePath,
        });
      }
    }
  };

  useEffect(() => {
    if (
      table &&
      table.data &&
      table.data.details &&
      table.data.details.listData
    ) {
      let tempData: any[] = [];
      table.data.details.listData.map((item: any) => {
        let newTitle = "";

        if (
          titleColumn &&
          Array.isArray(titleColumn) &&
          titleColumn.length > 0
        ) {
          titleColumn.forEach(item2 => {
            if (item[item2]) {
              newTitle = `${newTitle} ${item[item2]}`;
            }
          });
        } else if (
          titleColumn &&
          typeof titleColumn === "string" &&
          item[titleColumn]
        ) {
          newTitle = item[titleColumn];
        } else {
          newTitle = "-";
        }

        tempData = [
          ...tempData,
          {
            id: item[idColumn] ? item[idColumn] : null,
            title: newTitle.trim(),
            type: typeColumn && item[typeColumn] ? item[typeColumn] : null, //"-",
            type2: type2Column && item[type2Column] ? item[type2Column] : null, //"-",
            allData: item,
          },
        ];
      });

      let filteredTempData = filterCardData
        ? // For one column filter criterion

          tempData.filter(item => {
            let keep = true;
            const data = item.allData ?? null;

            if (data && typeof data === "object") {
              Object.entries(filterCardData).map(
                ([columnName, filterValue]) => {
                  if (
                    data.hasOwnProperty(columnName) &&
                    data[columnName] === filterValue
                  ) {
                    keep = false;
                  }
                }
              );
            }

            return keep;
          })
        : tempData;

      setCardData(filteredTempData);
    }
  }, [table]);

  if (table && table.loading) {
    return <Spinner />;
  }
  return (
    <div
      className={`CardPage CardPage-${layout}  ${!cardData ||
        (cardData && cardData.length === 0 ? "NoData" : "")}`}
    >
      <div className={`CardsContainer`}>
        {cardData &&
          cardData.length > 0 &&
          cardData.map((item, idx) => {
            if (item.id) {
              return (
                <React.Fragment key={`fragment-${idx}`}>
                  <CardPrototype
                    // menuData={menuData}
                    LargeRightPanel={LargeRightPanel}
                    footerActionType={actionType}
                    data={item.allData}
                    title={item.title ?? ""}
                    projectID={projectID}
                    clientID={clientID}
                    type={
                      includeTypeColumn ? (item.type ? item.type : "-") : null
                    }
                    type2={
                      includeType2Column ? (item.type2 ? item.type2 : "-") : null
                    }
                    dataId={item.id}
                    combineTitleAndType={combineTitleAndType}
                    hideMenuOptions={hideMenuOptions}
                    imageCardColumn={imageCardColumn}
                    index={idx}
                    fullscreenImage={fullScreenImage}
                    setFullscreenImage={setFullscreenImage}
                    overrideFooterButtonText={overrideFooterButtonText}
                    classname={contentClassname}
                    customContent={customContent && customContent(item.allData)}
                    key={`card-prototype-${idx}`}
                    maxTitleLength={maxTitleLength ?? undefined}
                    centerCardPageTitle={centerCardPageTitle}
                    hideIcon={hideIcon}
                    crudItems={crudItems}
                    permissions={permissions}
                    disableMenuItemData={disableMenuItemData}
                    rolePermissions={rolePermissions}
                    oppositePermissions={oppositePermissions}
                    idColumn={idColumn}
                    table={table as TableInfo}
                    customIcon={customIcon && customIcon(item.allData)}
                  />
                </React.Fragment>
              );
            } else {
              return null;
            }
          })}
      </div>

      {fullScreenImage && (
        <Transition in={delayedOpen} timeout={100} mountOnEnter unmountOnExit>
          {state => (
            <div
              className={"ModalUnderlay CardPageFileViewer FileViewer " + state}
            >
              <div className={"Modal FullSize FileViewer"} {...bind}>
                <div className="CloseIcon">
                  <img
                    src={closeIcon}
                    alt="close file preview"
                    onClick={() => setFullscreenImage(null)}
                  />
                </div>

                <div className="Title">
                  <div className="Text">
                    {`${
                      cardData &&
                      fullScreenImage &&
                      cardData[fullScreenImage.index].title
                        ? cardData[fullScreenImage.index].title
                        : ""
                    }${
                      cardData &&
                      fullScreenImage &&
                      cardData[fullScreenImage.index].type
                        ? ` - ${cardData[fullScreenImage.index].type}`
                        : ""
                    } `}
                  </div>
                </div>

                <button
                  className={`PrevImage ${
                    fullScreenImage.index === 0 ? "Disabled" : ""
                  }`}
                  disabled={fullScreenImage.index === 0}
                  onClick={previousImage}
                >
                  <img src={leftArrowIcon} alt="next" />
                </button>

                {fullScreenImage.filePath ? (
                  <img
                    className="FullScreenImage"
                    src={fullScreenImage.filePath}
                    alt="full size image"
                  />
                ) : (
                  <div className="NoImage">
                    <div className="Text">No image</div>
                  </div>
                )}

                <button
                  className={`NextImage ${
                    fullScreenImage.index + 1 === cardData?.length
                      ? "Disabled"
                      : ""
                  }`}
                  disabled={fullScreenImage.index + 1 === cardData?.length}
                  onClick={nextImage}
                >
                  <img src={rightArrowIcon} alt="next" />
                </button>
              </div>
            </div>
          )}
        </Transition>
      )}
    </div>
  );
}

export default CardPage;
